/* eslint-disable @typescript-eslint/no-explicit-any */
import * as he from "he";
import React, { useCallback, useEffect, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Modal as ReactstrapModal, ModalBody, ModalHeader } from "reactstrap";
import { Menu, MenuItem } from "../../state/types/menuTypes";
import { useModifierSelection } from "../../hooks/useModifierSelection";
import ModifierGroupSelector from "../ModifierSelector/ModifierGroupSelector";
import Asset from "../Asset/Asset";

export interface IMenuItemModalProps {
  isOpen: boolean;
  menuItem: MenuItem;
  menu: Menu;
  onClose: () => void;
  onAddToOrder: (item: MenuItem, selectedOptions: any[]) => void;
}

const MenuItemModal: React.FC<IMenuItemModalProps> = ({
  isOpen,
  menuItem,
  menu,
  onClose,
  onAddToOrder,
}) => {
  const {
    selectedOptions,
    setSelectedOption,
    resetToDefaults,
    getUnselectedRequiredModifierGroupIds,
    deriveModifierGroups,
  } = useModifierSelection({
    menu,
    modifierGroupIds: menuItem.modifierGroupsIds,
  });

  const [
    unselectedRequiredModifierGroupIds,
    setUnselectedRequiredModifierGroupIds,
  ] = useState<number[]>(getUnselectedRequiredModifierGroupIds());

  const [modifierGroupIdsToBeHighlighted, setModifierGroupIdsToBeHighlighted] =
    useState<number[]>([]);

  const [highlightTrigger, setHighlightTrigger] = useState(0);

  useEffect(() => {
    if (isOpen) {
      resetToDefaults();
    }
  }, [isOpen, resetToDefaults]);

  useEffect(() => {
    setUnselectedRequiredModifierGroupIds(
      getUnselectedRequiredModifierGroupIds()
    );
  }, [selectedOptions]);

  const handleAddToOrder = useCallback(() => {
    setModifierGroupIdsToBeHighlighted(unselectedRequiredModifierGroupIds);
    setHighlightTrigger((prev) => prev + 1);

    if (unselectedRequiredModifierGroupIds.length <= 0) {
      onAddToOrder(menuItem, selectedOptions);
    }
  }, [
    menuItem,
    selectedOptions,
    onAddToOrder,
    unselectedRequiredModifierGroupIds,
  ]);

  const modifierGroupsDerived = deriveModifierGroups();

  return (
    <>
      <ReactstrapModal
        isOpen={isOpen}
        toggle={onClose}
        className="menu-item-modal"
        centered
      >
        <ModalHeader toggle={onClose}>{menuItem.name}</ModalHeader>
        <ModalBody>
          <div className="menu-item-modal-content">
            <div className="menu-item-modal-item-info">
              {menuItem.displayAssetId && (
                <div className="menu-item-modal-image">
                  <Asset
                    thumbnailAssetId={menuItem.displayAssetId}
                    transform={"200x"}
                  />
                </div>
              )}
              {menuItem.description && (
                <div className="menu-item-modal-description">
                  {he.decode(menuItem.description)}
                </div>
              )}
              <div className="menu-item-modal-price">
                {menuItem.price.currencySymbol}
                <FormattedNumber
                  minimumFractionDigits={2}
                  value={menuItem.price.amount}
                />
              </div>
            </div>

            <div className="menu-item-modal-options">
              <ModifierGroupSelector
                modifierGroups={modifierGroupsDerived}
                selectedOptions={selectedOptions}
                onModifierSelect={setSelectedOption}
                modifierGroupIdsToBeHighlighted={
                  modifierGroupIdsToBeHighlighted
                }
                highlightTrigger={highlightTrigger}
              />
            </div>

            <div className="menu-item-modal-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddToOrder}>
                Add to Order
              </button>
            </div>
          </div>
        </ModalBody>
      </ReactstrapModal>
    </>
  );
};

export default MenuItemModal;
